<template>
  <component
    :is="isGridDisabled ? 'div' : 'grid-layout'"
    class="grid-layout"
    :class="{
      'grid-layout--landing': isLandingSkin,
      'grid-layout--disabled': isGridDisabled,
      'grid-layout--editing': isEditable,
    }"
    :layout.sync="layout"
    :col-num="colNum"
    :row-height="30"
    :margin="[10, 10]"
    :is-draggable="isEditable"
    :is-resizable="isEditable"
    :is-mirrored="false"
    vertical-compact
    :responsive="false"
    auto-size
    use-css-transforms
    @layout-updated="layoutUpdatedEvent"
  >
    <component
      :is="isGridDisabled ? 'div' : 'grid-item'"
      v-for="item in isGridDisabled ? mobileLayout : layout"
      :key="item.i"
      :x="item.x"
      :y="item.y"
      :w="item.w"
      :h="item.h"
      :i="item.i"
      class="grid-item"
      :class="[
        `grid-item--${item.i}`,
        getLayoutItemContainerProps(item).class || '',
      ]"
      v-bind="getLayoutItemContainerProps(item)"
      @click.native="handleGridItemClick($event, item)"
    >
      <component
        :is="components[item.appKey]"
        class="w-100 h-100"
        :app="item.app"
        v-bind="item.props || {}"
      />
    </component> 
  </component>
</template>

<script>
import VueGridLayout from 'vue-grid-layout';
// import { GridLayout, GridItem } from 'vue-grid-layout/src/components';
// import { BCard } from 'bootstrap-vue';
import {
  ENABLED_APPS_ACTIONS,
  ENABLED_APPS_GETTERS,
  ENABLED_APPS_MUTATIONS,
} from '@/store/enabled-apps/enabled-apps-store-constants';
import Vue from 'vue';
import GridStatusMixin from '@core/mixins/apps-layout/GridStatusMixin';
import { defaultLayoutDimensionsForElement } from '@/@core/utils/element-api';
import { trackCardClick, trackButtonClick, getNombreFlujo } from '@/utils/analytics';
// import { randomString } from '@/@core/utils/utils';

export default {
  name: 'AppsLayout',
  props: {
    elems: {
      type: [Array, Object],
      required: false,
    },
  },
  components: {
    // GridLayout: () => import('vue-grid-layout/src/components/GridLayout' /* webpackChunkName: "GridLayout" */),
    // GridItem: () => import('vue-grid-layout/src/components/GridItem' /* webpackChunkName: "GridItem" */),
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem,
    // BCard,
  },
  mixins: [GridStatusMixin],
  inject: ['$chatBot'],
  data() {
    return {
      components: {},
      isLoadingComponents: true,
      // NOTE: I need to use local layout because VueGridLayout is modifying the layout,
      // even if I don't use `.sync`.
      layout: [],
      unsubscribeAction: null,
    };
  },
  computed: {
    savedLayout() {
      return this.isLoadingComponents
        ? []
        : this.$store.getters[ENABLED_APPS_GETTERS.savedLayout];
    },
    mobileLayout() {
      return this.$store.getters[ENABLED_APPS_GETTERS.mobileLayout];
    },
    enabledApps() {
      return this.$store.getters[ENABLED_APPS_GETTERS.enabledApps];
    },
    elements() {
      return this.$store.getters.elements.unpaginated;
    },
    addon() {
      return this.$store.getters.addon;
    },
    componentsLoaders() {
      return this.$store.getters[ENABLED_APPS_GETTERS.componentsLoaders];
    },
    isEditable() {
      return this.$store.getters[ENABLED_APPS_GETTERS.isEditingLayout];
    },
    colNum() {
      return 12;
    },
    defaultWidth() {
      return 6;
    },
    defaultHeight() {
      return 10;
    },
  },
  watch: {
    savedLayout(newLayout) {
      this.layout = JSON.parse(JSON.stringify(newLayout));
    },
    isEditable(isEditable) {
      if (!isEditable) {
        this.layout = JSON.parse(JSON.stringify(this.savedLayout));
        this.$chatBot.show();
      } else {
        this.$chatBot.hide();
      }
    },
    addon(newVal) {
      if (newVal.unpaginated.length === 0 || this.addon.unpaginated.length === 0) {
        return;
      }
      if (newVal.unpaginated.length > 0) {
        const indexAppToRemove = this.layout.findIndex((app) => app.appKey === newVal.unpaginated[0].addonKey);
        const newLayout = [...this.layout];
        newLayout.splice(indexAppToRemove, 1);
        this.layout = JSON.parse(JSON.stringify(newLayout));

        this.updateLayout();

        this.$store.commit('SET_ITEM_LITERAL', {
          itemType: 'addon',
          data: {
            paginated: {},
            unpaginated: [],
          },
        });
      }
    },
    async elements(newVal) {
      const event = Array.isArray(newVal) ? newVal[0] : newVal;
      if (!event?.key || !(event.created || event.edited || event.deleted)) {
        return;
      }

      const elementKey = event.key;
      const loadElementsWidget = () => import('@/views/apps/elements/widgets/ElementsListWidget.vue');

      if (event.created) {
        if (this.layout.some((item) => item.appKey === elementKey)) {
          this.clearElementsQueue();
          return;
        }

        const dims = defaultLayoutDimensionsForElement(event);
        const layoutItem = {
          itemKey: elementKey,
          ...event,
          y: -0.1,
          x: 0,
          h: dims.h,
          w: dims.w,
          i: elementKey,
          appKey: elementKey,
          props: { element: event },
        };
        const widgetModule = await loadElementsWidget();
        Vue.set(this.components, elementKey, widgetModule.default);
        this.layout = [...this.layout, layoutItem];
        this.$store.commit('upsertCollectiveElement', event);
      }

      if (event.edited) {
        const itemIndex = this.layout.findIndex((x) => x.appKey === elementKey);
        if (itemIndex !== -1) {
          const layoutItem = {
            ...this.layout[itemIndex],
            ...event,
            props: { element: { ...event } },
          };
          const widgetModule = await loadElementsWidget();
          Vue.set(this.components, elementKey, widgetModule.default);
          const newLayout = [...this.layout];
          newLayout[itemIndex] = layoutItem;
          this.layout = newLayout;
          this.$store.commit('upsertCollectiveElement', event);
        }
      }

      if (event.deleted) {
        const itemIndex = this.layout.findIndex((x) => x.appKey === elementKey);
        if (itemIndex !== -1) {
          const newLayout = [...this.layout];
          newLayout.splice(itemIndex, 1);
          this.layout = newLayout;
          Vue.delete(this.components, elementKey);
        }
        this.$store.commit('markCollectiveElementDeleted', elementKey);
      }

      this.upsertElementsKeysList(elementKey, !!event.deleted);
      this.clearElementsQueue();
      this.updateLayout();
    },
  },
  async created() {
    // console.log('START created APPS-LAYOUT....')
    this.isLoadingComponents = true;

    // const force = !!this.$store.getters.currentCollective.isEvent;
    await Promise.all(
      Object.entries(this.componentsLoaders).map(
        async ([appKey, loadComponent]) => {
          const component = await loadComponent();
          this.components[appKey] = component;
        },
      ),
    );

    // NOTE: I need to use this subscription because because VueGridLayout is modifying the layout,
    // even if I don't use `.sync`.
    this.unsubscribeAction = this.$store.subscribeAction({
      before: ({ type }) => {
        if (type === ENABLED_APPS_ACTIONS.saveLayout) {
          this.updateLayout();
        }
      },
    });

    this.isLoadingComponents = false;
    // this.$nextTick(() => {
    //   this.scheduleAutoHeightAdjustment();
    // });
  },

  beforeDestroy() {
    if (this.unsubscribeAction) this.unsubscribeAction();
  },

  methods: {
    adjustItemsHeightToContent() {
      if (this.isGridDisabled || !this.$el) return;

      const ROW_HEIGHT = 30;
      const V_MARGIN = 10;

      const updatedLayout = this.layout.map(item => {
        const gridItemEl = this.$el.querySelector(`.grid-item--${item.i}`);
        if (!gridItemEl) return item;

        const contentEl = gridItemEl.querySelector('.widget') || gridItemEl;
        if (!contentEl) return item;

        const contentHeight = contentEl.scrollHeight;
        if (!contentHeight) return item;

        const totalRowSize = ROW_HEIGHT + V_MARGIN;
        const neededRows = Math.max(
          item.h || 0,
          Math.ceil((contentHeight + V_MARGIN) / totalRowSize),
        );

        if (neededRows === item.h) return item;

        return {
          ...item,
          h: neededRows,
        };
      });

      const hasChanges = updatedLayout.some((item, index) => item.h !== this.layout[index].h);
      if (!hasChanges) return;

      this.layout = JSON.parse(JSON.stringify(updatedLayout));
      this.updateLayout();
    },
    scheduleAutoHeightAdjustment() {
      if (this.isGridDisabled) return;

      clearTimeout(this._autoHeightTimeout);
      this._autoHeightTimeout = setTimeout(() => {
        this.$nextTick(() => this.adjustItemsHeightToContent());
      }, 200);
    },
    clearElementsQueue() {
      this.$store.commit('SET_ITEM_LITERAL', {
        itemType: 'elements',
        data: {
          paginated: {},
          unpaginated: [],
        },
      });
    },
    upsertElementsKeysList(key, isDeleted) {
      const current = this.$store.getters.elementsKeysList?.unpaginated || [];
      const byKey = new Map();
      current.forEach((entry) => {
        if (entry?.key) {
          byKey.set(entry.key, entry);
        }
      });
      byKey.set(key, {
        key,
        ...(isDeleted ? { isDeleted: true } : {}),
      });
      this.$store.commit('SET_ITEM_LITERAL', {
        itemType: 'elementsKeysList',
        data: {
          paginated: {},
          unpaginated: Array.from(byKey.values()),
        },
      });
    },
    updateLayout() {
      this.$store.commit(ENABLED_APPS_MUTATIONS.updateLayout, this.layout);
    },
    getLayoutItemContainerProps(item) {
      return (this.isGridDisabled ? item.containerProps : item.gridProps) || {};
    },

    layoutUpdatedEvent (newLayout) {
      if (newLayout.length) {
        this.$emit('reloaded', true)
      }
      // this.scheduleAutoHeightAdjustment();
    },
    /**
     * Maps Nexos grid item IDs to their elemento names for analytics
     * @param {string} itemId - The grid item ID
     * @returns {string|null} - The elemento name or null if not a tracked item
     */
    getNexosCardElemento(itemId) {
      const nexosCardMap = {
        'image-Nycgh4EkvoyyHVh': 'laboratorio de ideas',
        'image-Zb4ayR3Z1caGJEI': 'hablemos',
        'image-noDhd7YOBwrnD5x': 'conocenos',
      };
      return nexosCardMap[itemId] || null;
    },
    /**
     * Gets button tracking info for Nexos grid items
     * @param {string} itemId - The grid item ID
     * @returns {Object|null} - Object with elemento, ubicacion, and optional nombreFlujo, or null if not a tracked button
     */
    getNexosButtonInfo(itemId) {
      const nexosButtonMap = {
        'cta2-6uiNGHXjO6n2pHR': {
          elemento: 'quiero saber',
          ubicacion: 'todo lo que tienes que saber del laboratorio de ideas',
        },
        'cta-xlBua3jt0oSTaMt': {
          elemento: 'nuestras reglas',
          ubicacion: 'botones inferiores',
          nombreFlujo: 'conocenos',
        },
        'cta-j4efTV1WiHAmep9': {
          elemento: 'preguntas frecuentes',
          ubicacion: 'botones inferiores',
          nombreFlujo: 'conocenos',
        },
        'cta-ZtgdHcChr49xg5h': {
          elemento: 'terminos y condiciones',
          ubicacion: 'botones inferiores',
          nombreFlujo: 'conocenos',
        },
        'cta-In098AtCMLUSa10': {
          elemento: 'tratamiento de datos',
          ubicacion: 'botones inferiores',
          nombreFlujo: 'conocenos',
        },
        'cta-aLVKHdrvei6SAyy': {
          elemento: 'sobre cookies',
          ubicacion: 'botones inferiores',
          nombreFlujo: 'conocenos',
        },
      };
      return nexosButtonMap[itemId] || null;
    },
    /**
     * Handles click events on grid items for Nexos analytics tracking
     * @param {Event} event - The click event
     * @param {Object} item - The grid item object
     */
    handleGridItemClick(event, item) {
      if (!item || !item.i) return;
      
      // Check if it's a tracked button
      const buttonInfo = this.getNexosButtonInfo(item.i);
      if (buttonInfo) {
        const nombreFlujo = buttonInfo.nombreFlujo || getNombreFlujo(this.$route);
        trackButtonClick(nombreFlujo, buttonInfo.elemento, buttonInfo.ubicacion);
        return;
      }
      
      // Check if it's a tracked card
      const elemento = this.getNexosCardElemento(item.i);
      if (elemento) {
        trackCardClick('home', elemento, 'inicio');
      }
    }
  },
};
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/bootstrap-include"; // Bootstrap includes

@include media-breakpoint-up(lg) {
  .grid-layout {
    margin: -10px;
  }
}

.grid-layout {
  min-height: 1000px !important;
}

.grid-item {
  overflow: hidden;
  border-radius: 12px;
  //min-height: 200px !important; //eso estaba haciendo que se solaparan los widgets en los elements...
}
.grid-layout--landing {
  display: flex;
  flex-wrap: wrap;
  .grid-item {
    flex-basis: 100%;
    &--chat,
    &--liveStreaming {
      flex-basis: 50%;
    }
  }
}
.grid-layout--disabled {
  .grid-item {
    margin-bottom: $spacer;
    &--posts > div,
    &--communication > div {
      max-height: 100vh;
    }
  }
}

.grid-layout--editing {
  > .grid-item {
    box-shadow: 0px 0px 10px -2px $primary;
    border-radius: $card-border-radius;
    &.resizing {
      border-radius: none;
      border: 3px solid $primary;
    }
    &::v-deep > .vue-resizable-handle {
      bottom: 8px;
      right: 8px;
      background-position: center;
      background-size: 47%;
    }
  }
}

@media (max-width: 996px) {
  ::v-deep .grid-item[class*="grid-item--image"] > div > div > .background-image-container {
    // min-height: 180px !important;
    width: 100% !important;
    height: auto !important;
    // aspect-ratio: 1.5 / 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  ::v-deep .background-image-container {
    // min-height: 180px !important;
    width: 100% !important;
    height: auto !important;
    // aspect-ratio: 1.5 / 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>